import { fromUnixTime, getTime } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { type ZodType, type ZodTypeDef, z } from "zod";

export type DataPoint<TValue = null | number> = readonly [timestamp: Time, value: TValue];

/**
 * A narrower internal type.
 * The wider external {@link DataPoint} type enforces graceful Time handling.
 */
type DataPointJSON<TValue = null | number> = readonly [timestamp: number, value: TValue];

const TIMESTAMP_SCHEMA = z.number().transform((time) => getTime(fromUnixTime(time)));
const DEFAULT_VALUE_SCHEMA = z.coerce.number().nullable().catch(null);

export function DataPoint<TValue>(timestamp: Time, value: TValue): DataPointJSON<TValue> {
	return [getTime(timestamp), value];
}

function fromUnix(values: ReadonlyArray<null | number | string>): null | DataPointJSON;

function fromUnix<TValue>(
	values: ReadonlyArray<null | number | string>,
	valueSchema: ZodType<TValue, ZodTypeDef, unknown>,
): null | DataPointJSON<TValue>;

function fromUnix<TValue>(
	values: ReadonlyArray<null | number | string>,
	valueSchema?: ZodType<TValue, ZodTypeDef, unknown>,
): null | DataPointJSON | DataPointJSON<TValue> {
	if (valueSchema == null) {
		return fromUnix(values, DEFAULT_VALUE_SCHEMA);
	}

	return z.tuple([TIMESTAMP_SCHEMA, valueSchema]).safeParse(values).data ?? null;
}

DataPoint.fromUnix = fromUnix;
